import React, { useState } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Shield, CheckCircle, Send, ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';

const PARTNER_CATEGORIES = [
  'Books & Courses',
  'Events & Experiences',
  'Social Groups',
  'Coaching',
  'Volunteering',
  'Fitness & Social Sport',
  'Other',
];

const Partners = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [form, setForm] = useState({
    orgName: '',
    contactName: '',
    email: '',
    website: '',
    category: '',
    audienceSize: '',
    description: '',
    commissionProposal: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiClient.request('/api/partners/apply', {
        method: 'POST',
        body: JSON.stringify({
          org_name: form.orgName,
          contact_name: form.contactName,
          email: form.email,
          website: form.website,
          category: form.category,
          audience_size: form.audienceSize,
          description: form.description,
          commission_proposal: form.commissionProposal,
        }),
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Could not submit your application. Please try again.');
    }
    setSubmitting(false);
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (submitted) {
    return (
      <DashboardLayout>
        <div className="p-4 sm:p-8 max-w-2xl mx-auto text-center py-16" data-testid="partner-success">
          <CheckCircle size={56} className="mx-auto text-green-500 mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Application Received</h1>
          <p className="text-gray-600 mb-6">
            Thank you for your interest in partnering with HiBFF. Our team will review your application against our mission test and respond within 5 business days.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            <strong>Our mission test:</strong> &quot;Does this help a HiBFF user connect with a real person in the real world?&quot;
          </p>
          <button
            onClick={() => navigate('/marketplace')}
            className="px-6 py-3 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors"
          >
            Back to Marketplace
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-2xl mx-auto" data-testid="partner-application-page">
        <button onClick={() => navigate('/marketplace')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ArrowLeft size={16} /> Back to Marketplace
        </button>

        <div className="text-center mb-8">
          <Shield size={40} className="mx-auto text-purple-500 mb-3" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Become a HiBFF Partner</h1>
          <p className="text-gray-600 max-w-lg mx-auto">
            We partner with organisations that help people connect with real humans in the real world. If that&apos;s you, we&apos;d love to hear from you.
          </p>
        </div>

        {/* Mission Test */}
        <div className="mb-8 p-4 bg-purple-50 border border-purple-200 rounded-xl" data-testid="mission-test">
          <p className="text-sm font-bold text-purple-900 mb-1">The one rule that governs every listing:</p>
          <p className="text-purple-800 font-medium italic">&quot;Does this help a HiBFF user connect with a real person in the real world?&quot;</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Name *</label>
              <input
                required
                value={form.orgName}
                onChange={(e) => updateField('orgName', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                data-testid="partner-org-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name *</label>
              <input
                required
                value={form.contactName}
                onChange={(e) => updateField('contactName', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                data-testid="partner-contact-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                required type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                data-testid="partner-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input
                value={form.website}
                onChange={(e) => updateField('website', e.target.value)}
                placeholder="https://"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                data-testid="partner-website"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Partner Category *</label>
            <select
              required
              value={form.category}
              onChange={(e) => updateField('category', e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent bg-white"
              data-testid="partner-category"
            >
              <option value="">Select a category</option>
              {PARTNER_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Audience Size (approximate)</label>
            <input
              value={form.audienceSize}
              onChange={(e) => updateField('audienceSize', e.target.value)}
              placeholder="e.g. 10,000 monthly users"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
              data-testid="partner-audience"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">How does your product/service help people connect in real life? *</label>
            <textarea
              required
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              rows={4}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent resize-none"
              data-testid="partner-description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commission / Revenue Share Proposal</label>
            <input
              value={form.commissionProposal}
              onChange={(e) => updateField('commissionProposal', e.target.value)}
              placeholder="Tell us what you have in mind — we'll discuss what works for both sides"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-300 focus:border-transparent"
              data-testid="partner-commission"
            />
          </div>

          {submitError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 text-center" data-testid="partner-submit-error">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="partner-submit"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {submitting ? 'Submitting…' : 'Submit Application'}
          </button>

          <p className="text-xs text-gray-400 text-center">
            All applications are reviewed against HiBFF&apos;s mission by our partnerships team. We respond within 5 business days — every application is read personally.
          </p>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default Partners;
